import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Theme,
  CreateThemeDto,
  UpdateThemeDto,
} from '../../../../core/interfaces/theme';
import { Subject } from '../../../../core/interfaces/subject';
import {
  CustomSelectComponent,
  SelectOption,
} from '../../../../components/ui/custom-select/custom-select.component';

@Component({
  selector: 'app-add-theme',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    CustomSelectComponent,
  ],
  templateUrl: './add-theme.component.html',
  styleUrl: './add-theme.component.css',
})
export class AddThemeComponent implements OnInit, OnChanges {
  @Input() theme: Theme | null = null;
  @Input() isEditMode = false;
  /** Matière pré-sélectionnée (valeur par défaut du champ, pas une contrainte). */
  @Input() subjectId: number | null = null;
  /**
   * Matières proposées. Le choix se fait **ici** et non via le filtre de la
   * liste : créer un thème ne doit pas dépendre de l'état d'un filtre.
   * Chaque libellé porte son parcours pour lever l'ambiguïté des homonymes.
   */
  @Input() subjects: Array<Subject & { learningPathTitle?: string }> = [];

  @Output() saved = new EventEmitter<CreateThemeDto | UpdateThemeDto>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);

  themeForm!: FormGroup;

  /** Options du sélecteur recherchable — matérialisées (cf. add-professor). */
  subjectOptions: SelectOption[] = [];

  ngOnInit(): void {
    this.initializeForm();
    this.rebuildSubjectOptions();
    this.populateFormIfEdit();
  }

  private rebuildSubjectOptions(): void {
    this.subjectOptions = this.subjects.map((s) => ({
      id: s.id,
      title: this.subjectLabel(s),
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subjects']) this.rebuildSubjectOptions();
    if (changes['theme'] && !changes['theme'].firstChange) {
      this.populateFormIfEdit();
    }
    if (changes['isEditMode'] && !changes['isEditMode'].firstChange) {
      this.populateFormIfEdit();
    }
    if (changes['subjectId'] && !changes['subjectId'].firstChange) {
      this.populateFormIfEdit();
    }
  }

  private initializeForm(): void {
    this.themeForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      subjectId: [this.subjectId, [Validators.required]],
      displayOrder: [0, [Validators.required, Validators.min(0)]],
      isActive: [true],
    });
  }

  private populateFormIfEdit(): void {
    if (this.isEditMode && this.theme) {
      this.themeForm.patchValue({
        title: this.theme.title,
        description: this.theme.description,
        // La matière du thème prime sur celle suggérée par la liste.
        subjectId: this.theme.subjectId ?? this.subjectId,
        displayOrder: this.theme.displayOrder,
        isActive: this.theme.isActive,
      });
    } else if (!this.isEditMode) {
      // Reset form when switching to add mode
      this.themeForm.reset({
        title: '',
        description: '',
        subjectId: this.subjectId,
        displayOrder: 0,
        isActive: true,
      });
    }
  }

  /** Libellé d'une matière, suffixé de son parcours quand on le connaît. */
  subjectLabel(subject: Subject & { learningPathTitle?: string }): string {
    return subject.learningPathTitle
      ? `${subject.name} — ${subject.learningPathTitle}`
      : subject.name;
  }

  // Update the onSubmit method in add-theme.component.ts:

  onSubmit(): void {
    if (this.themeForm.invalid) {
      this.themeForm.markAllAsTouched();
      return;
    }

    const formValue = this.themeForm.value;
    // La matière vient du formulaire : le champ est obligatoire, le
    // `themeForm.invalid` plus haut a déjà écarté le cas manquant.
    const subjectId = Number(formValue.subjectId);

    if (this.isEditMode && this.theme) {
      // For edit mode: include the id in the DTO
      const updateData: UpdateThemeDto = {
        id: this.theme.id,
        title: formValue.title,
        description: formValue.description,
        displayOrder: Number(formValue.displayOrder), // Ensure it's a number
        isActive: Boolean(formValue.isActive), // Ensure it's a boolean
        subjectId,
      };
      this.saved.emit(updateData);
    } else {
      // For create mode: no id needed
      const createData: CreateThemeDto = {
        title: formValue.title,
        description: formValue.description,
        displayOrder: Number(formValue.displayOrder), // Ensure it's a number
        isActive: Boolean(formValue.isActive), // Ensure it's a boolean
        subjectId,
      };
      this.saved.emit(createData);
    }
  }

  onCancel(): void {
    this.themeForm.reset({
      title: '',
      description: '',
      subjectId: this.subjectId,
      displayOrder: 0,
      isActive: true,
    });
    this.cancelled.emit();
  }

  get title() {
    return this.themeForm.get('title');
  }

  get description() {
    return this.themeForm.get('description');
  }

  get displayOrder() {
    return this.themeForm.get('displayOrder');
  }

  get isActive() {
    return this.themeForm.get('isActive');
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.themeForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getErrorMessage(fieldName: string): string {
    const field = this.themeForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) {
      return this.translate.instant('VALIDATION.REQUIRED');
    }
    if (field.errors['minlength']) {
      return this.translate.instant('VALIDATION.MIN_LENGTH', {
        min: field.errors['minlength'].requiredLength,
      });
    }
    if (field.errors['min']) {
      return this.translate.instant('VALIDATION.MIN_VALUE', {
        min: field.errors['min'].min,
      });
    }

    return '';
  }
}
